import { useQuery } from '@tanstack/react-query'
import { supabase } from '../supabaseClient'

export function useEstablishments(showArchived = false, organizationId = null, establishmentType = 'mxb') {
  return useQuery({
    queryKey: ['establishments', showArchived, organizationId, establishmentType],
    queryFn: async () => {
      // Fetch establishments without inline inspection history
      const allData = []
      const batchSize = 1000
      let offset = 0

      while (true) {
        let query = supabase
          .from('establishments')
          .select(`
            *,
            users:assigned_inspector_id(first_name, last_name)
          `)
          .eq('active', !showArchived)
          .eq('establishment_type', establishmentType)
          .order('mxb_number')
          .range(offset, offset + batchSize - 1)

        if (organizationId) query = query.eq('organization_id', organizationId)

        const { data, error } = await query
        if (error) throw error
        if (!data || data.length === 0) break
        allData.push(...data)
        if (data.length < batchSize) break
        offset += batchSize
      }

      // Fetch last inspection date per establishment via RPC (single query, much faster than waterfall)
      const estIds = allData.map(e => e.id)
      const lastInspectionMap = {}

      const batchSize500 = 500
      for (let i = 0; i < estIds.length; i += batchSize500) {
        const idBatch = estIds.slice(i, i + batchSize500)
        const { data: lastDates, error: rpcError } = await supabase
          .rpc('get_last_inspection_dates', { est_ids: idBatch })

        if (rpcError) {
          console.error('get_last_inspection_dates RPC failed:', rpcError)
        } else if (lastDates) {
          for (const row of lastDates) {
            lastInspectionMap[row.establishment_id] = row.inspection_date
          }
        }
      }

      // Attach last inspection date to each establishment
      const enriched = allData.map(est => ({
        ...est,
        inspections: lastInspectionMap[est.id]
          ? [{ inspection_date: lastInspectionMap[est.id], is_new_opening: false }]
          : []
      }))

      const uniqueCities = [...new Set(enriched.map(e => e.city).filter(Boolean))].sort()
      const uniqueAgents = [...new Set(
        enriched.filter(e => e.users).map(e => `${e.users.first_name} ${e.users.last_name}`)
      )].sort()

      return {
        establishments: enriched,
        cities: uniqueCities,
        agents: uniqueAgents,
        totalRecords: enriched.length
      }
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
  })
}
